import { derived, get } from 'svelte/store';
import { transferLoaders } from '../transfers-loader.js';
import { TransferEngine } from './engine';
import { IndexedQueueStorage } from './storage';
import type { Transfer, TransferInput, Json } from './types';

const OWNER_KEY = 'huckleberry.transfers.owner';
let initialized: Promise<void> | undefined;
let channel: BroadcastChannel | undefined;
let timer: ReturnType<typeof setTimeout>;
let paused = false;
let lastProgress = 0;
const engine = new TransferEngine(new IndexedQueueStorage(), transferLoaders, {
  online: () => !paused && navigator.onLine,
  identity: async () => {
    const response = await fetch('/user-authentication/api/user', { credentials: 'include', cache: 'no-store', signal: AbortSignal.timeout(8000) });
    if (response.status === 401) { sessionStorage.removeItem(OWNER_KEY); return null; }
    if (!response.ok) throw new Error('Verbindung zum Server nicht verfügbar.');
    const data = await response.json();
    const owner = String(data.user?.id || data.id || '');
    if (!owner) throw new Error('Benutzer konnte nicht geprüft werden.');
    sessionStorage.setItem(OWNER_KEY, owner); return owner;
  },
  changed: () => { channel?.postMessage({ type: 'changed' }); schedule(1000); },
  completed: job => {
    window.dispatchEvent(new CustomEvent('huckleberry-transfer-completed', { detail: job }));
    channel?.postMessage({ type: 'completed', id: job.id });
  },
  progress: event => {
    if (Date.now() - lastProgress < 200 && event.state !== 'confirming') return;
    lastProgress = Date.now(); channel?.postMessage({ type: 'progress', event });
  },
});

function schedule(delay = 5000) {
  clearTimeout(timer);
  timer = setTimeout(async () => {
    if (!paused) await engine.pump();
    const pending = get(engine.jobs).some(job => !['draft', 'done', 'error', 'local_error'].includes(job.state));
    schedule(pending ? 5000 : 30000);
  }, delay);
}
export function startTransfers(): Promise<void> {
  return initialized ||= (async () => {
    try { engine.setOwner(sessionStorage.getItem(OWNER_KEY)); } catch { /* Private storage may be unavailable. */ }
    try { await engine.authenticate(); } catch (error) { engine.error.set((error as Error).message); }
    try { await engine.refresh(); } catch { engine.error.set('Gerätespeicher nicht verfügbar. Neue Daten sind noch nicht gesichert.'); }
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel('huckleberry-transfers');
      channel.onmessage = event => {
        if (event.data?.type === 'logout') { paused = true; engine.pause(); sessionStorage.removeItem(OWNER_KEY); }
        if (event.data?.type === 'changed') void engine.refresh().catch(() => {});
        if (event.data?.type === 'progress') engine.receiveProgress(event.data.event);
        if (event.data?.type === 'completed') void engine.refresh().then(() => {
          const job = get(engine.jobs).find(job => job.id === event.data.id && job.state === 'done');
          if (job) window.dispatchEvent(new CustomEvent('huckleberry-transfer-completed', { detail: job }));
        }).catch(() => {});
        if (event.data?.type === 'identity') { paused = false; void engine.authenticate().then(() => engine.pump(true)).catch(() => {}); }
      };
    }
    const resume = () => { if (!paused) void engine.pump(true); };
    window.addEventListener('online', resume);
    window.addEventListener('focus', resume);
    window.addEventListener('pageshow', resume);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) resume(); });
    window.addEventListener('huckleberry-authenticated', () => {
      paused = false; channel?.postMessage({ type: 'identity' });
      void engine.authenticate().then(() => engine.pump(true)).catch(() => {});
    });
    window.addEventListener('huckleberry-logout', pauseTransfers);
    // Release active requests/leases on full navigation; receipts handle uncertain outcomes.
    window.addEventListener('pagehide', () => engine.pause());
    window.addEventListener('pageshow', () => { if (!paused) void engine.authenticate().then(() => engine.pump(true)).catch(() => {}); });
    schedule(); void engine.pump(true);
  })();
}
export function pauseTransfers() {
  paused = true; engine.pause();
  try { sessionStorage.removeItem(OWNER_KEY); } catch { /* no stored owner */ }
  channel?.postMessage({ type: 'logout' });
}

/** Feature views never receive another feature's records. This is API scoping, not an origin security boundary. */
export function featureTransfers(feature: string) {
  const jobs = derived(engine.jobs, items => items.filter(job => job.feature === feature));
  return {
    jobs, error: derived([jobs, engine.error], ([items, error]) => items.some(job => !job.discardedAt && !['draft', 'done'].includes(job.state)) ? error : ''),
    ready: startTransfers,
    enqueue: async (input: TransferInput) => { await startTransfers(); const result = await engine.save(feature, input); navigator.storage?.persist?.().catch(() => false); return result; },
    saveDraft: async (input: TransferInput) => { await startTransfers(); const result = await engine.save(feature, { ...input, draft: true }, true); navigator.storage?.persist?.().catch(() => false); return result; },
    release: (id: string, build: (root: Transfer, children: Transfer[]) => Json) => engine.release(feature, id, build),
    retry: (id: string) => engine.retry(feature, id),
    remove: (id: string) => engine.remove(feature, id),
    discard: (id: string, cleanup: 'none' | 'cancel' = 'none') => engine.discard(feature, id, cleanup),
    files: (id: string) => engine.files(feature, id),
  };
}

/** Only the central framework overview consumes this unscoped view. */
export const transferOverview = { jobs: engine.jobs, error: engine.error, ready: startTransfers,
  retry: (job: Transfer) => engine.retry(job.feature, job.id), remove: (job: Transfer) => engine.remove(job.feature, job.id),
  files: (job: Transfer) => engine.files(job.feature, job.id) };
